import { Box, Checkbox, FormControlLabel, Grid, MenuItem, Select, TextField } from "@mui/material";
import downloadjs from "downloadjs";
import * as React from "react";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { ConfirmationDialogResult, EnumHelper, WhistleBlowSource } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { IModifyUser, ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IWhistleBlowEditItem } from "ui/Scripts/source/models/whistleBlow/whistleBlowEditItem";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import * as attachmentService from "../../../models/services/attachmentService";
import * as whistleBlowService from "../../../models/services/whistleBlowService";
import { ISessionState } from "../../../models/session/sessionState";
import * as storeActions from "../../../models/store/storeActions";
import { UserListItems } from "../../../models/user/userListItems";
import { AttachmentList } from "../../attachments/AttachmentList";
import { DatePicker } from "../../framework/DateTime/DatePicker";
import { TimePicker } from "../../framework/DateTime/TimePicker";
import { Dialog } from "../../framework/Dialog/Dialog";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { SearchField } from "../../framework/SearchField";
import { SelectUnitDialog } from "../../framework/Unit/SelectUnitDialog";
import { UserBlocks } from "../../user/UserBlocks/UserBlocks";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";

export interface IWhistleBlowNewDialogProp {
    sessionState: ISessionState;
    classes?: string;
    onOk: (whistleBlowId: string) => void;
    onCancel: () => void;
}

export interface IWhistleBlowNewDialogState {
    isLoading: boolean;
    whistleBlow: IWhistleBlowEditItem;
    canReadInformantData: boolean;
    units: IIdTitleNode[];
    unitId: string;
    unitCode: string;
    source: WhistleBlowSource;
    sourceComment: string;
    anonymous: boolean;
    title: string;
    dateStr: string;
    timeStr: string;
    description: string;
    informantName: string;
    informantEmail: string;
    informantPhone: string;
    informantPasswordConfirmed: boolean;
    attachments: IFileAttachment[];
    handlers: IModifyUser[];
    showSelectUnitDialog: boolean;
}

export class WhistleBlowNewDialog extends React.Component<IWhistleBlowNewDialogProp, IWhistleBlowNewDialogState> {
    private orgStateHash: string = "";

    constructor(props: IWhistleBlowNewDialogProp) {
        super(props);
        this.state = {
            isLoading: false,
            whistleBlow: null,
            canReadInformantData: false,
            units: [],
            unitId: "",
            unitCode: "",
            source: WhistleBlowSource.Unspecified,
            sourceComment: "",
            anonymous: true,
            title: "",
            dateStr: "",
            timeStr: "",
            description: "",
            informantName: "",
            informantEmail: "",
            informantPhone: "",
            informantPasswordConfirmed: false,
            attachments: [],
            handlers: [],
            showSelectUnitDialog: false
        };
    }

    loadWhistleBlow = async (): Promise<boolean> => {
        const props = this.props;
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => whistleBlowService.getWhistleBlowEdit(Base.emptyGuid), false, false, () => this.setState({ isLoading: false }));
        if (!result) return false;
        const whistleBlow = result.whistleBlow;
        const state = {
            ...this.state,
            whistleBlow,
            canReadInformantData: result.canReadInformantData,
            units: result.units,
            categories: result.categories,
            unitId: whistleBlow.unitId,
            unitCode: whistleBlow.unitCode,
            categoryId: whistleBlow.categoryId,
            state: whistleBlow.state,
            stateStr: EnumHelper.WhistleBlowStateToStr(whistleBlow.state),
            severity: whistleBlow.severity,
            resolution: whistleBlow.resolution,
            resolutionComment: whistleBlow.resolutionComment,
            source: whistleBlow.source,
            sourceComment: whistleBlow.sourceComment,
            code: whistleBlow.code,
            anonymous: whistleBlow.anonymous,
            title: whistleBlow.title,
            dateStr: Base.timeToDateStr(whistleBlow.date),
            timeStr: Base.timeToTimeStr(whistleBlow.date),
            description: whistleBlow.description,
            informantName: whistleBlow.informantName,
            informantEmail: whistleBlow.informantEmail,
            informantPhone: whistleBlow.informantPhone,
            informantPasswordConfirmed: whistleBlow.informantPasswordConfirmed,
            attachments: whistleBlow.attachments,
            publicMessages: whistleBlow.messages.filter(i => !i.isInternal),
            internalMessages: whistleBlow.messages.filter(i => i.isInternal),
            handlers: ModifyUser.sortModifyUsers(whistleBlow.handlers, { currentUserCode: props.sessionState.code })
        };
        this.setState(state);
        const saveData = await this.getSaveDataFromState(props, state);
        this.orgStateHash = saveData.hash;
        return result.canReadInformantData;
    };

    componentDidMount(): void {
        this.loadWhistleBlow();
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "title") {
            this.setState({ title: value });
        } else if (name === "description") {
            this.setState({ description: value });
        } else if (name === "unitId") {
            this.setState({ unitId: value });
        } else if (name === "source") {
            this.setState({ source: value.toInteger() });
        } else if (name === "sourceComment") {
            this.setState({ sourceComment: value });
        } else if (name === "informantName") {
            this.setState({ informantName: value });
        } else if (name === "informantEmail") {
            this.setState({ informantEmail: value });
        } else if (name === "informantPhone") {
            this.setState({ informantPhone: value });
        }
    };

    handleDateChange = (value: string) => {
        this.setState({ dateStr: value });
    };

    handleTimeChange = (value: string) => {
        this.setState({ timeStr: value });
    };

    // #region Attachments
    handleAttachmentDownload = async (id: string) => {
        if (!id) return;
        const state = this.state;
        const attachment = state.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    handleAttachmentRemove = async (category: number, id: string) => {
        if (!id) return;
        const state = this.state;
        this.setState({
            attachments: state.attachments.filter(i => i.id !== id)
        });
    };
    
    handleAttachmentsAdd = async (category: number, fileList: FileList) => {
        const state = this.state;
        const attachments = await FileAttachment.addFileListToFileAttachments([], fileList, category);
        if (attachments.length < 1) return;
        this.setState({
            attachments: state.attachments.concat(attachments)
        });
    };
    // #endregion Attachments

    // #region Select Unit
    handleSelectUnit = () => {
        this.setState({
            showSelectUnitDialog: true
        });
    };

    handleSelectUnitDialogOk = async (node: IIdTitleNode) => {
        if (!node) return;
        const result = await AppUtils.callService(() => whistleBlowService.getDefaultWhistleBlowHandlersForUnit(node.id));
        if (!result) return null;
        this.setState({
            showSelectUnitDialog: false,
            unitId: node.id,
            unitCode: node.title,
            handlers: ModifyUser.sortModifyUsers(result.items, { currentUserCode: this.props.sessionState.code })
        });
    };

    handleSelectUnitDialogCancel = () => {
        this.setState({
            showSelectUnitDialog: false
        });
    };
    // #endregion Select Unit

    // #region Handlers
    handleAddHandlers = async (users: IModifyUser[]): Promise<ApiSuccess> => {
        const state = this.state;
        if (!users || users.length < 1) return null;
        this.setState({
            handlers: ModifyUser.sortModifyUsers(state.handlers.concat(users), { currentUserCode: this.props.sessionState.code })
        });
        return Base.getPromiseResult(new ApiSuccess());
    };

    handleRemoveHandlers = async (userCodes: string[]): Promise<ApiSuccess> => {
        const state = this.state;
        if (!userCodes || userCodes.length < 1) return null;
        this.setState({
            handlers: state.handlers.filter(i => userCodes.indexOf(i.code) < 0)
        });
        return Base.getPromiseResult(new ApiSuccess());
    };
    
    handleGetHandlersSelectListItems = (companyId: string, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, _ignoreQrCodeUsers: boolean, _requireQrCodeUsers: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
        const state = this.state;
        return whistleBlowService.getHandlersSelectUserListItems(state.whistleBlow.id, state.unitId, pageSize, page, filter,
            ignoreUserIds, ignoreUserCodes, activityState, ignoreRecordUserConnected, doNotIgnoreRecordUserConnectedUserCode,
            sortColumn, sortOrderIsAsc);
    };
    // #endregion Handlers

    getSaveDataFromState = async (props: IWhistleBlowNewDialogProp, state: IWhistleBlowNewDialogState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("unitId", state.unitId);
        result.append("date", Base.getDateTime(state.dateStr, state.timeStr)?.getTime() ?? 0);
        result.append("title", state.title);
        result.append("description", state.description);
        result.append("source", state.source);
        result.append("sourceComment", state.sourceComment);
        result.append("anonymous", state.anonymous);
        result.append("informantName", state.informantName);
        result.append("informantEmail", state.informantEmail);
        result.append("informantPhone", state.informantPhone);
        result.append("handlerUserCodes", state.handlers.map(i => i.code));
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(state.attachments));
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.title) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.TitleMustBeDefined));
                return resolve(false);
            }
            if (!state.dateStr) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.DateMustBeDefined));
                return resolve(false);
            }
            if (!state.timeStr) {
                AppUtils.showErrorMessage(Translations.TimeMustBeDefined);
                return resolve(false);
            }
            if (!state.description) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.DescriptionMustBeDefined));
                return resolve(false);
            }
            if (state.handlers.length < 1) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.HandlersMustBeDefined));
                return resolve(false);
            }
            const dateTime = Base.getDateTime(state.dateStr, state.timeStr).getTime();
            if (dateTime > Base.getNowTicks()) {
                AppUtils.showErrorMessage(Translations.WhistleBlowDateAndTimeCannotBeInFuture);
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async (): Promise<SaveSuccess> => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        const saveData = await this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => whistleBlowService.saveWhistleBlowNew(saveData.obj));
        if (!result) return null;
        this.orgStateHash = saveData.hash;
        return result;
    };

    handleSave = async () => {
        const props = this.props;
        const result = await this.saveToDb();
        if (!result) return;
        props.onOk(result.id);
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave();
        } else {
            props.onCancel();
        }
    };

    render() {
        const props = this.props;
        const state = this.state;
        const whistleBlow = state.whistleBlow;
        if (!whistleBlow) return null;
        return (
            <Dialog
                id="wb-new-dialog"
                maxWidth="lg"
                title={Translations.AddWhistleBlow}
                body={
                    <>
                        <GridRow>
                            <Grid item sm={6}>
                                <DialogSectionCard headerTitle={Translations.WhistleBlowInformation}>
                                    <FormField title={Translations.Title} required>
                                        <TextField type="text" name="title" title={Translations.Title} inputProps={{ maxLength: 100 }} value={state.title} onChange={this.handleChange} size="small" fullWidth />
                                    </FormField>
                                    <FormField title={Translations.WhistleBlowDateAndTime} required>
                                        <GridRow>
                                            <Grid item sm={6}>
                                                <DatePicker
                                                    required={true}
                                                    value={state.dateStr}
                                                    onChange={this.handleDateChange}
                                                    // onBlur={this.handleDateChange}
                                                />
                                            </Grid>
                                            <Grid item sm={6}>
                                                <TimePicker
                                                    required={true}
                                                    value={state.timeStr}
                                                    onChange={this.handleTimeChange}
                                                    // onBlur={this.handleTimeChange}
                                                />
                                            </Grid>
                                        </GridRow>
                                    </FormField>
                                    <FormField title={Translations.WhistleBlowDescription} required>
                                        <TextField name="description" title={Translations.WhistleBlowDescription} inputProps={{ maxLength: 1000 }} value={state.description} onChange={this.handleChange} multiline rows={5} size="small" fullWidth />
                                    </FormField>
                                    <FormField title="">
                                        <FormControlLabel
                                            label={Translations.WhistleBlowAnonymousReport}
                                            control={
                                                <Checkbox
                                                    checked={state.anonymous}
                                                    onChange={(_event, checked: boolean) => {
                                                        this.setState({ anonymous: checked });
                                                    }}
                                                />
                                            }
                                            slotProps={{ typography: { variant: "body2" } }}
                                        />
                                    </FormField>
                                    {!state.anonymous &&
                                        <>
                                            <FormField title={Translations.Name}>
                                                <TextField type="text" name="informantName" title={Translations.Name} inputProps={{ maxLength: 100 }} value={state.informantName} onChange={this.handleChange} size="small" fullWidth />
                                            </FormField>
                                            <FormField title={Translations.EmailAddress}>
                                                <TextField type="text" name="informantEmail" title={Translations.EmailAddress} inputProps={{ maxLength: 100 }} value={state.informantEmail} onChange={this.handleChange} size="small" fullWidth />
                                            </FormField>
                                            <FormField title={Translations.Phone}>
                                                <TextField type="text" name="informantPhone" title={Translations.Phone} inputProps={{ maxLength: 50 }} value={state.informantPhone} onChange={this.handleChange} size="small" fullWidth />
                                            </FormField>
                                        </>
                                    }
                                    <AttachmentList
                                        //classes={state.anonymous ? "withAnonymous" : ""}
                                        title={<label className="control-label">{Translations.Attachments}</label>}
                                        attachments={state.attachments.filter(i => i.category === 0)}
                                        onRemoveAttachment={(id) => this.handleAttachmentRemove(0, id)}
                                        onDownloadAttachment={this.handleAttachmentDownload}
                                        onAddAttachments={(fileList) => this.handleAttachmentsAdd(0, fileList)}
                                    />
                                </DialogSectionCard>
                            </Grid>
                            <Grid item sm={6}>
                                <DialogSectionCard headerTitle={Translations.Handling}>
                                    <FormField title={Translations.Unit}>
                                        <SearchField value={state.unitCode} disabled buttonTitle={Translations.Select} onSubmit={this.handleSelectUnit} />
                                    </FormField>
                                    <UserBlocks
                                        companyId={props.sessionState.companyId}
                                        title={Translations.Handlers}
                                        noUsersMessage={Translations.AddHandlers}
                                        parentIsNew={false}
                                        parentIsNewMessage={Translations.NewWhistleBlowMustBeSavedBeforeHandlersCanBeAdded}
                                        users={state.handlers}
                                        removeUsersConfirmation={Translations.DoYouReallyWantToRemoveSelectedHandlers}
                                        onAddUsers={this.handleAddHandlers}
                                        onRemoveUsers={this.handleRemoveHandlers}
                                        onGetUserSelectListItems={this.handleGetHandlersSelectListItems}
                                    />
                                    <FormField title={Translations.Source}>
                                        <Select name="source" title={Translations.Source} value={state.source.toString(10)} onChange={this.handleChange} size="small" fullWidth>
                                            <MenuItem key={WhistleBlowSource.Unspecified} value={WhistleBlowSource.Unspecified.toString(10)}>{Translations.WhistleBlowSourceUnspecified}</MenuItem>
                                            <MenuItem key={WhistleBlowSource.System} value={WhistleBlowSource.System.toString(10)}>{Translations.WhistleBlowSourceSystem}</MenuItem>
                                            <MenuItem key={WhistleBlowSource.Phone} value={WhistleBlowSource.Phone.toString(10)}>{Translations.WhistleBlowSourcePhone}</MenuItem>
                                            <MenuItem key={WhistleBlowSource.Email} value={WhistleBlowSource.Email.toString(10)}>{Translations.WhistleBlowSourceEmail}</MenuItem>
                                            <MenuItem key={WhistleBlowSource.Mail} value={WhistleBlowSource.Mail.toString(10)}>{Translations.WhistleBlowSourceMail}</MenuItem>
                                            <MenuItem key={WhistleBlowSource.Oral} value={WhistleBlowSource.Oral.toString(10)}>{Translations.WhistleBlowSourceOral}</MenuItem>
                                            <MenuItem key={WhistleBlowSource.Other} value={WhistleBlowSource.Other.toString(10)}>{Translations.WhistleBlowSourceOther}</MenuItem>
                                        </Select>
                                    </FormField>
                                    <FormField title={Translations.SourceDescription}>
                                        <TextField name="sourceComment" title={Translations.SourceDescription} inputProps={{ maxLength: 1000 }} value={state.sourceComment} onChange={this.handleChange} multiline rows={5} size="small" fullWidth />
                                    </FormField>
                                </DialogSectionCard>
                            </Grid>
                        </GridRow>
                        {state.showSelectUnitDialog &&
                            <SelectUnitDialog
                                nodes={state.units}
                                title={Translations.SelectUnit}
                                firstColumnTitle={Translations.Unit}
                                selectedId={state.unitId}
                                onOk={this.handleSelectUnitDialogOk}
                                onCancel={this.handleSelectUnitDialogCancel}
                            />
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="end" width="100%">
                        <DialogButtons
                            isNew={true}
                            onCancel={this.handleCancel}
                            onSave={this.handleSave}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}